import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import GeoZones from '../apis/apiGeoZones';
import { addCenter } from '../utils/utils';

export const FENCE_MAP_ZOOM_DEF = 5;
export const FENCE_MAP_ZOOM_SINGLE = 15;

// Initial state for fences
const fenceInitialState = {
  geoZones: [],
  selectedZone: [],
  handleMapFence: false,
  zoomLevel: FENCE_MAP_ZOOM_DEF,
  drawingMode: null,
  fenceFilters: {
    search: '',
    sort: 'ASC',
  },
  addFenceData: {
    addFence: false,
    fenceDetails: {},
    deleteConfirm: false,
    deleteId: '',
  },
};

// using async thunk for fetching GeoZones
export const fetchGeoZones = createAsyncThunk('fence/fetchGeoZones', async () => {
  try {
    const response = await GeoZones.apiGeoZonesGet();
    return response;
  } catch (error) {
    console.error('Error fetching GeoZones: ', error);
    throw error;
  }
});

// using async thunk for adding GeoZones
export const addGeoZone = createAsyncThunk('fences/addFence', async (newFence) => {
  try {
    if (newFence.type !== 'circle') {
      newFence.center = addCenter(newFence.type, newFence.bounds ? newFence.bounds : newFence.coords);
    }
    const response = await GeoZones.apiGeoZoneCreate(newFence);
    return response;
  } catch (error) {
    console.error('Error adding fence: ', error);
    throw error;
  }
});

// using async thunk for deleting GeoZones
export const deleteGeoZone = createAsyncThunk('fences/deleteFence', async (fence) => {
  try {
    await GeoZones.apiGeoZonesDelete(fence);
    return fence.entityId;
  } catch (error) {
    console.error('Error deleting fence: ', error);
    throw error;
  }
});

// using async thunk for updating GeoZones
export const updateGeoZone = createAsyncThunk('fences/updateFence', async (fence) => {
  try {
    if (fence.type !== 'circle' && fence.nameEdit === false) {
      fence.center = addCenter(fence.type, fence.bounds ? fence.bounds : fence.coords);
    }
    console.log(fence, 'fence');
    await GeoZones.apiGeoZonesUpdate(fence);
    return fence;
  } catch (error) {
    console.error('Error updating fence: ', error);
    throw error;
  }
});

// using async thunk for showing selected GeoZone
export const selectedGeoZone = createAsyncThunk('fence/selectedFence', async (fence) => {
  return fence;
});

// using async thunk for deleting selected GeoZone
export const deleteSelectedGeoZone = createAsyncThunk('fence/deleteSelectedFence', async (fence) => {
  return fence;
});

// using async thunk for updating GeoZone Filters
export const updateFenceFilters = createAsyncThunk('fence/updateFenceFilters', async (filters) => {
  return filters;
});

// using async thunk for handling addFenceData
export const handleAddFenceData = createAsyncThunk('fence/handleAddFenceData', async (data) => {
  return data;
});

// using async thunk for clearing selectedZone
export const clearSelectedZone = createAsyncThunk('fence/clearSelectedZone', async () => {
  return [];
});

// using async thunk for drawing mode
export const drawMode = createAsyncThunk('fence/drawMode', async (data) => {
  return data;
});

// using async thunk for selectall handling
export const selectAllHandling = createAsyncThunk('fence/selectAll', async (data) => {
  return data;
});

// slice
const fenceSlice = createSlice({
  name: 'fence',
  initialState: fenceInitialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchGeoZones.fulfilled, (state, action) => {
        state.geoZones = action.payload; // setting the state with fetched GeoZones
      })
      .addCase(addGeoZone.fulfilled, (state, action) => {
        if (state.geoZones.length === 0) {
          state.geoZones.push(action.payload);
        } else {
          state.geoZones[0] = action.payload; // Adding the new geofence to geoZones array
        }
        state.addFenceData.addFence = false;
        state.addFenceData.fenceDetails = {};
      })
      .addCase(deleteGeoZone.fulfilled, (state, action) => {
        // Remove the deleted geofence by filtering it out from geoZones
        state.geoZones = state.geoZones.filter((zone) => zone.entityId !== action.payload);
      })
      .addCase(updateGeoZone.fulfilled, (state, action) => {
        // Update the geofence in geoZones
        state.geoZones = state.geoZones.map((zone) => {
          // If the entityId matches, update the zone with the new data
          if (zone.entityId === action.payload.entityId) {
            return {
              ...zone,
              ...action.payload,
            };
          }
          return zone; // Return the original zone if no match
        });

        state.selectedZone = state.selectedZone.map((zone) => {
          // If the entityId matches, update the zone with the new data
          if (zone.entityId === action.payload.entityId) {
            return {
              ...zone,
              ...action.payload,
            };
          }
          return zone; // Return the original zone if no match
        });
      })
      .addCase(selectedGeoZone.fulfilled, (state, action) => {
        if (state.selectedZone.length === 0) {
          state.selectedZone.push(action.payload);
          state.drawingMode = null;
          state.handleMapFence = false;
        } else {
          const existingIndex = state.selectedZone.findIndex((zone) => zone.entityId === action.payload.entityId);
          if (existingIndex === -1) {
            state.selectedZone[0] = action.payload;
            state.handleMapFence = false;
          } else {
            state.selectedZone.splice(existingIndex, 1);
            state.handleMapFence = true;
          }
        }
      })
      .addCase(deleteSelectedGeoZone.fulfilled, (state, action) => {
        state.selectedZone = state.selectedZone.filter((zone) => zone.entityId !== action.payload.entityId);
      })
      .addCase(updateFenceFilters.fulfilled, (state, action) => {
        state.fenceFilters = action.payload;
      })
      .addCase(handleAddFenceData.fulfilled, (state, action) => {
        state.addFenceData = action.payload;
      })
      .addCase(clearSelectedZone.fulfilled, (state, action) => {
        state.selectedZone = action.payload;
      })
      .addCase(drawMode.fulfilled, (state, action) => {
        state.drawingMode = action.payload;
      })
      .addCase(selectAllHandling.fulfilled, (state, action) => {
        state.selectedZone = action.payload;
      });
  },
});

export default fenceSlice.reducer;
